import React from 'react';
import PropTypes from 'prop-types';
import { LuArrowRight, LuListTodo } from 'react-icons/lu';
import GlassIcon from '../../../components/ui/GlassIcon';
import { useTranslation } from '../../../i18n/LanguageContext';

const ICON_GRADIENT = {
  calm: 'tw-from-teal-400 tw-to-cyan-500 tw-shadow-teal-500/30',
  unseen: 'tw-from-rose-500 tw-to-orange-400 tw-shadow-rose-500/30',
};

/** "Yapılacaklar Listesi" call to action; louder while there are unseen tasks. */
const TasksBanner = ({ unseenCount, onOpen }) => {
  const { t } = useTranslation();
  const hasUnseen = unseenCount > 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={[
        'tw-group tw-relative tw-isolate tw-flex tw-w-full tw-flex-col tw-gap-4 tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-p-5 tw-text-left',
        'tw-transition-[border-color,box-shadow] tw-duration-300 sm:tw-flex-row sm:tw-items-center sm:tw-gap-5 sm:tw-p-6',
        'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60',
        hasUnseen
          ? 'tw-border-rose-200/80 tw-bg-white/85 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_18px_40px_-24px_rgba(244,63,94,0.55)] hover:tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_22px_48px_-22px_rgba(244,63,94,0.65)] dark:tw-border-rose-400/25 dark:tw-bg-[#22262f]/85'
          : 'tw-border-white/80 tw-bg-white/70 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_16px_40px_-28px_rgba(15,23,42,0.35)] hover:tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_44px_-24px_rgba(15,23,42,0.4)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={`tw-pointer-events-none tw-absolute -tw-left-16 -tw-top-24 -tw-z-10 tw-h-56 tw-w-56 tw-rounded-full tw-blur-3xl tw-transition-opacity tw-duration-500 group-hover:tw-opacity-100 ${hasUnseen ? 'tw-bg-rose-300/40 tw-opacity-80 dark:tw-bg-rose-500/20' : 'tw-bg-teal-300/40 tw-opacity-60 dark:tw-bg-teal-500/15'}`}
      />

      <span className="tw-relative tw-shrink-0">
        <GlassIcon gradient={hasUnseen ? ICON_GRADIENT.unseen : ICON_GRADIENT.calm} size="lg">
          <LuListTodo aria-hidden="true" className="tw-h-7 tw-w-7" />
        </GlassIcon>
        {hasUnseen && (
          <span className="tw-absolute -tw-right-1.5 -tw-top-1.5 tw-grid tw-h-6 tw-min-w-[1.5rem] tw-place-items-center tw-rounded-full tw-bg-rose-500 tw-px-1.5 tw-text-xs tw-font-bold tw-text-white tw-ring-2 tw-ring-white dark:tw-ring-[#22262f]">
            {unseenCount > 99 ? '99+' : unseenCount}
          </span>
        )}
      </span>

      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{t('dashboard.tasks.title')}</span>
          {hasUnseen && (
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-rose-500/10 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-rose-600 tw-ring-1 tw-ring-inset tw-ring-rose-500/20 dark:tw-text-rose-300">
              <span className="tw-relative tw-flex tw-h-2 tw-w-2">
                <span className="tw-absolute tw-inline-flex tw-h-full tw-w-full tw-animate-ping tw-rounded-full tw-bg-rose-400/75 motion-reduce:tw-animate-none" />
                <span className="tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-rose-500" />
              </span>
              {t('dashboard.tasks.newBadge', { count: unseenCount })}
            </span>
          )}
        </span>
        <span className="tw-mt-1 tw-block tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-400">
          {hasUnseen ? t('dashboard.tasks.unseen', { count: unseenCount }) : t('dashboard.tasks.calm')}
        </span>
      </span>

      <span className="tw-relative tw-inline-flex tw-shrink-0 tw-items-center tw-gap-2 tw-self-start tw-overflow-hidden tw-rounded-full tw-border tw-border-white/50 tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-px-5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30 tw-transition-transform tw-duration-200 group-active:tw-scale-[0.97] sm:tw-self-center dark:tw-border-white/25">
        <span
          aria-hidden="true"
          className="tw-pointer-events-none tw-absolute tw-inset-0 tw-rounded-[inherit] tw-bg-[radial-gradient(120%_90%_at_30%_0%,rgba(255,255,255,0.55),rgba(255,255,255,0)_55%)] tw-shadow-[inset_0_1px_1px_rgba(255,255,255,0.85),inset_0_-2px_6px_rgba(8,47,73,0.3)]"
        />
        <span className="tw-relative tw-drop-shadow-[0_1px_1px_rgba(8,47,73,0.35)]">{t('common.view')}</span>
        <LuArrowRight aria-hidden="true" className="tw-relative tw-h-4 tw-w-4 tw-transition-transform tw-duration-300 group-hover:tw-translate-x-1" />
      </span>
    </button>
  );
};

TasksBanner.propTypes = {
  unseenCount: PropTypes.number.isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default TasksBanner;
